import { Box, Divider, Grid, Tooltip, Typography } from '@mui/material';
import { useEffect, useState, lazy, Suspense, useMemo } from 'react';
import InfoGreyIcon from '../../assets/icon-grey-info.svg';
import colors from 'assets/theme/base/colors';
import moment from 'moment';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import DashboardTileComponent from './DashboardTileComponent';

import { ReactComponent as DataObservabilityLandingIcon } from '../../assets/PepWise2.0/Data Observability Landing.svg';
import { ReactComponent as DataEstateLandingIcon } from '../../assets/PepWise2.0/D&AI Executive Landing.svg';
import { ReactComponent as DataXopsLandingIcon } from '../../assets/PepWise2.0/D&AI Xops Landing.svg';
import { ReactComponent as ProgramObsLandingIcon } from '../../assets/PepWise2.0/Program Landing.svg';
import { ReactComponent as RMLandingIcon } from '../../assets/PepWise2.0/FinOps Dashboards Landing.svg';
import { ReactComponent as PlatformLandingIcon } from '../../assets/PepWise2.0/Platform Landing.svg';
import { ReactComponent as MLLandingIcon } from '../../assets/PepWise2.0/Machine Learning Landing.svg';
import { ReactComponent as KPIandAlertsLandingIcon } from '../../assets/PepWise2.0/Data KPIs & Alerts Landing.svg';
import { ReactComponent as ProductCatalogLandingIcon } from '../../assets/PepWise2.0/Product Catalog.svg';
import { ReactComponent as ProductSuccessLandingIcon } from '../../assets/PepWise2.0/Product_Success.svg';
import { ReactComponent as DataOpsIcon } from '../../assets/PepWise2.0/DataOps.svg';
import { ReactComponent as ProgramOpsIcon } from '../../assets/PepWise2.0/ProgramOps.svg';
import { ReactComponent as SectorExecutiveIcon } from '../../assets/PepWise2.0/SectorExecutive.svg';
import { ReactComponent as TerradataIcon } from '../../assets/PepWise2.0/simple-icons_teradata.svg';
import { ReactComponent as TableauIcon } from '../../assets/PepWise2.0/simple-icons_tableau.svg';
import { ReactComponent as DataGrowthIcon } from '../../assets/PepWise2.0/material-symbols_chart-data.svg';
import { ReactComponent as DataBricksIcon } from '../../assets/PepWise2.0/simple-icons_databricks.svg';
import { ReactComponent as PrestoIcon } from '../../assets/PepWise2.0/simple-icons_presto.svg';
import { ReactComponent as PowerBIIcon } from '../../assets/PepWise2.0/simple-icons_powerBI.svg';
import { ReactComponent as DataManagementIcon } from '../../assets/PepWise2.0/simple-icons_DataManagement.svg';
import { ReactComponent as EnterprisePlatformIcon } from '../../assets/PepWise2.0/Enterprise Platform Modernization Dashboard.svg';
import { ReactComponent as IbpIcon } from '../../assets/PepWise2.0/IBP.svg';
import { ReactComponent as MdipIcon } from '../../assets/PepWise2.0/MDIP.svg';
import { ReactComponent as RMIcon } from '../../assets/PepWise2.0/Resource Mapping.svg';
import { ReactComponent as PlatformCostIcon } from '../../assets/PepWise2.0/Platform Cost.svg';
import { ReactComponent as KPIStoreIcon } from '../../assets/PepWise2.0/KPI Store.svg';
import { ReactComponent as AlertIcon } from '../../assets/PepWise2.0/Alerts.svg';
import { ReactComponent as StorageWhiteIcon } from '../../assets/PepWise2.0/Storage_White.svg';
import { ReactComponent as StorageIcon } from '../../assets/PepWise2.0/Storage.svg';

import { ReactComponent as QueryWhiteIcon } from '../../assets/PepWise2.0/Query_White.svg';
import { ReactComponent as QueryIcon } from '../../assets/PepWise2.0/Query.svg';

import { ReactComponent as IncidentsWhiteIcon } from '../../assets/PepWise2.0/Incidents_White.svg';
import { ReactComponent as IncidentsIcon } from '../../assets/PepWise2.0/Incidents.svg';

import { ReactComponent as ExecutiveSummaryWhiteIcon } from '../../assets/PepWise2.0/Executive Summary_White.svg';
import { ReactComponent as ExecutiveSummaryIcon } from '../../assets/PepWise2.0/Executive Summary.svg';

import { ReactComponent as DataProcessingWhiteIcon } from '../../assets/PepWise2.0/Data Processing_White.svg';
import { ReactComponent as DataProcessingIcon } from '../../assets/PepWise2.0/Data Processing.svg';

import { ReactComponent as CostWhiteIcon } from '../../assets/PepWise2.0/Cost_White.svg';
import { ReactComponent as CostIcon } from '../../assets/PepWise2.0/Cost.svg';

import { ReactComponent as BIApplicationWhiteIcon } from '../../assets/PepWise2.0/BI Application_White.svg';
import { ReactComponent as BIApplicationIcon } from '../../assets/PepWise2.0/BI Application.svg';

import RecentVisited from './RecentVisited';
import PepwiseJoyride from 'components/PepwiseJoyride';
import type { Placement } from 'react-joyride';
// import { hideTeradata, hideML, hideIBPExec, hideRMPlatformCost, hideMDIPExec } from '../../service/config'
import { hideTeradata, hideML, hideIBPExec, hideRMPlatformCost, hidePresto, hideDMF, hideDG, hideMDIPExec, isEnvTypePROD } from '../../service/config'
import { FINOPS_ALL_ROLES } from 'components/FinOpsExecutive/finopsRoles'
import PlatformHealth from '../../assets/PepWise2.0/platform-health-icon.svg';
import { ReactComponent as PipelineRunStatus } from '../../assets/PepWise2.0/Pipeline Run Status.svg';
import { RESTRICTED_ROLE_NAMES } from 'utils/common-util';
import { checkRole } from 'utils/roleUtils';
import { ReactComponent as TopicOfTheMonthIcon } from '../../assets/PepWise2.0/Topic of the Month.svg';
import { ReactComponent as PlatformIcon } from '../../assets/PepWise2.0/Platform.svg';
import { DataReconIcon } from 'components/CommonIcons/Icons';

// Lazy load components
const Header = lazy(() => import('./Header'));
const Footer = lazy(() => import('../LandingPage/LandingFooter'));


export const roleCollection = [
  "PwC_View",
  "PwC_Edit",
  "Infosys_View",
  "Infosys_Edit",
  "IBM_View",
  "IBM_Edit",
  "Admin"
];

export const removeCapabilities = [
  "Data KPI and Alerts",
];

const LandingPage = () => {
	const [memberData, setMemberData] = useState<any>(null);
	const [openTileId, setOpenTileId] = useState<string | null>(null);
	const [controller] = usePepwiseAuthController();
	const { accessToken, role, capabilities, isAuthenticated } = controller;

	const isRestricted = checkRole(role, RESTRICTED_ROLE_NAMES);

	// to display data
	useEffect(() => {
		setMemberData(accessToken);
	}, []);

	if(isAuthenticated){
		localStorage.setItem('capabilities', capabilities);
		localStorage.setItem('role', JSON.stringify(role));
	}

	const platformObsCapabilities = [
		'EDW Jobs',
		'Tableau',
		'Data Growth',
		'Databricks',
		'Presto',
		'Power BI',
		'Data Management Framework',
		'Controls & Compliance',
		'Verification & Validation'
	];

	const filteredCapabilities = useMemo(() => {
        if (!role || !capabilities) return capabilities;

        if (role.every((r: string) => roleCollection.includes(r))) {
            return capabilities.filter(
                (cap: string) => !removeCapabilities.includes(cap)
            );
        }

        return capabilities;
    }, [role, capabilities]);

	// Check if any of the required capabilities are included
	const isPlatformObsVisible = platformObsCapabilities.some((capability) => filteredCapabilities.includes(capability));

	const steps = [
			{
				target: "#sidebar-global",
				title: "Dashboard Navigation Panel",
				content: "Expand & quickly navigate to all PepWise dashboards to monitor and get insights.",
				isOpen: true,
				floaterProps: { placement: "right" as Placement, disableFlip: true, offset: 12 },
				disableBeacon: true,
			},
			{
				target: "#header-search-bar",
				title: "Global Search",
				content: "Search any keyword, will recommend and land you to related areas.",
				disableBeacon: true,
			},
			{
				target: "#header-user-icon",
				title: "User Profile Menu",
				content: "View your account details and quickly logout from the system. The profile icon also displays your user role and email.",
				disableBeacon: true,
			},
			{
				target: "#header-help-icon",
				title: "Help & Guided Tour",
				content: "Click this Help icon anytime to replay the guided tour or access user assistance.",
				disableBeacon: true,
			},
			{
				target: "#recent-dashboards",
				title: "Recently Used Dashboards",
				content: "Dashboards which you left before, will be shown here.",
				disableBeacon: true,
			},
			{
				target: "#dashboard-tiles",
				title: "Dashboard Tiles",
				content: "Dashboards provide access to various sub-modules and key analytics areas within PepWise.",
				disableBeacon: true,
			},
			{
				target: "#dashboard-plus-icon",
				title: "Expand Inner Modules",
				content: "Use the plus icon to expand and access inner modules in just a few clicks.",
				disableBeacon: true,
			},
		];
	
	return (
		<>
		<PepwiseJoyride steps={steps} runOnPaths={["/LandingPage"]} />
		<Box
			sx={{
				width: '100%',
				overflowY: 'auto',
				minHeight: '100vh',
				backgroundColor: `${colors.backgroundLight.primary}`,
				mb:1,
				'&::-webkit-scrollbar': {
					width: '8px !important'
				},
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: `${colors.scrollbar.thumb} !important`,
					borderRadius: '8px !important'
				},
				'&::-webkit-scrollbar-track': {
					backgroundColor: `${colors.scrollbar.background} !important`
				}
			}}
		>
			<Suspense fallback={<div>Loading...</div>}>
				{/* <Header memberData={memberData} /> */}
				<Grid sx={{ maxWidth: '95%', margin: '0 auto', mt: '80px' }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							alignItems: 'flex-start',
							mb: '12px',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								mb: "12px",
							}}
						>
							<Typography
								variant="mainTitle"
								sx={{
									color: `${colors.landingPage.welcomeText}`,
								}}
							>{`Welcome aboard, ${accessToken?.firstName} ${accessToken?.lastName}`}</Typography>
							<Typography
								variant='sectionHeader'
								sx={{
									mt: '9px',
									fontStyle: 'normal',
									color: `${colors.landingPage.subtitleText}`
								}}
							>
								Your PepWise journey starts here!
							</Typography>
						</Box>
						<Box
							sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'flex-start',
						}}
						>
							<Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
								{/* Label */}
								<Typography
									variant='bodyText'
									sx={{
										color: `${colors.landingPage.labelText}`,
										marginRight: '12px'
									}}
								>
									User Role
								</Typography>

								{/* Value */}
								{Array.isArray(role) && role.length > 1 ? (
									<>
										<Typography
											variant='bodyText'
											sx={{
												color: `${colors.landingPage.valueText}`,
												marginRight: '4px'
											}}
										>
											Multiple
										</Typography>
										<Tooltip 
											title={
												<Box>
													<Typography
														variant='bodyText'
														sx={{
															color: `${colors.landingPage.valueText}`,
															marginBottom: '8px'
														}}
													>
														Multiple Roles
													</Typography>
													{role.map((r) => (
														<Typography key={r.id || r} variant='bodyText' sx={{ color: `${colors.landingPage.valueText} !important` }}>
															{r}
														</Typography>
													))}
												</Box>
											}
											slotProps={{
												tooltip: {
												sx: {
													backgroundColor: `${colors.tooltip.background.primary} !important`,
													color: `${colors.tooltip.text.primary} !important`,
													boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
													padding: 1,
												},
												},
												arrow: {
												sx: {
													color: `${colors.tooltip.background.primary} !important`,
												},
												},
											}}
											arrow
										>
											<img
												src={InfoGreyIcon}
												alt="Role Information Icon"
												style={{ height: 15, marginLeft: 4, cursor: 'pointer' }}
											/>
										</Tooltip>
									</>
								) : (
									<Typography
										variant='bodyText'
										sx={{
											color: `${colors.landingPage.valueText}`,
										}}
									>
										{Array.isArray(role) && role.length === 1 ? role[0] : role?.name || '-'}
									</Typography>
								)}
							</Box>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									marginTop: '10px',
									mb: 1
								}}
							>
								{/* Sector Label */}
								<Typography
									variant='bodyText'
									sx={{
										color: `${colors.landingPage.labelText}`,
										marginRight: '38px'
									}}
								>
									Sector
								</Typography>

								{/* Sector Value */}
								<Typography
									variant='bodyText'
									sx={{
										color: `${colors.landingPage.valueText}`,
									}}
								>
									{accessToken?.sector.name || '-'}
								</Typography>
							</Box>

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<Typography
									variant='bodyText'
									sx={{
										color: `${colors.landingPage.labelText}`,
										marginRight: '8px'
									}}
								>
									Last Login
								</Typography>

								<Typography
									variant='bodyText'
									sx={{
										color: `${colors.landingPage.valueText}`,
									}}
								>
									{moment(memberData?.lastLogin).tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format('dddd, MMM DD, YYYY, hh:mm A z') || '-'}
								</Typography>
							</Box>
						</Box>
					</Box>

					<Box id="recent-dashboards" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
						<Typography variant= "sectionHeader" sx={{ color: `${colors.landingPage.sectionTitle} !important` }}>Recently Used Dashboards</Typography>
						<Divider sx={{ flexGrow: 1, backgroundColor: `${colors.landingPage.divider} !important`, marginLeft: 2 }} />
					</Box>

					<RecentVisited />

					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', mt: '16px' }}>
						<Typography variant= "sectionHeader" sx={{ color: `${colors.landingPage.sectionTitle} !important` }}>All Categories</Typography>
						<Divider sx={{ flexGrow: 1, backgroundColor: `${colors.landingPage.divider} !important`, marginLeft: 2 }} />
					</Box>

						<Grid container spacing={3} style={{ marginTop: '0px' }}>
							{filteredCapabilities.includes('Data Estate') && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dataestate/executive-summary')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/dataestate/executive-summary');
								// }}
							>
								<DashboardTileComponent
									id="D&AI-Executive-Dashboard"
									title="D&AI Executive Dashboard"
									icon={<DataEstateLandingIcon width="60px" height="60px" />}
									iconKey="D&AIExecutive"
									// route='/dataestate/executive-summary'
									open={openTileId === "D&AI-Executive-Dashboard"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "D&AI-Executive-Dashboard" ? null : "D&AI-Executive-Dashboard"
										)
									}
									childrenDashboards={[
											{
												id: "executive-summary",
												title: "Executive Summary",
												route: "/dataestate/executive-summary",
												// tagline: "High-level overview of enterprise data landscape.",
												icon: <ExecutiveSummaryIcon width={24} height={24} />,
												iconKey: "de-executive-summary"
											},
											{
												id: "storage-summary",
												title: "Storage",
												route: "/dataestate/storagesummary",
												// tagline: "Storage utilization & trends across platforms.",
												icon: <StorageIcon width={24} height={24} />,
												iconKey: "de-storage-summary"
											},
											{
												id: "queries-summary",
												title: "Query",
												route: "/dataestate/queriessummary",
												// tagline: "Query performance & workload insights.",
												icon: <QueryIcon width={24} height={24} />,
												iconKey: "de-queries-summary"
											},
											{
												id: "data-processing",
												title: "Data Processing",
												route: "/dataestate/data-processing",
												// tagline: "Processing metrics & job health.",
												icon: <DataProcessingIcon width={24} height={24} />,
												iconKey: "de-data-processing"
											},
											{
												id: "bi-summary",
												title: "BI Application",
												route: "/dataestate/biApplicationSummary",
												// tagline: "BI usage, adoption and insights.",
												icon: <BIApplicationIcon width={24} height={24} />,
												iconKey: "de-bi-summary"
											},
											{
												id: "cost-summary",
												title: "Cost",
												route: "/dataestate/costSummary",
												// tagline: "Enterprise data cost monitoring.",
												icon: <CostIcon width={24} height={24} />,
												iconKey: "de-cost-summary"
											},
											{
												id: "major-incident",
												title: "Major INC",
												route: "/dataestate/majorIncident",
												// tagline: "Track and analyze major data incidents.",
												icon: <IncidentsIcon width={24} height={24} />,
												iconKey: "de-major-incident"
											}
										]}
									/>
								</Grid>
							)}
							{(filteredCapabilities.includes('Data Estate', 'Fin Ops') || isPlatformObsVisible) && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/D&AIXOps/Summary')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/D&AIXOps/Summary');
								// }}
							>
								<DashboardTileComponent
									id="D&AI-Monthly-XOps"
									title="D&AI Monthly XOps Dashboards"
									icon={<DataXopsLandingIcon width="60px" height="60px" />}
									iconKey="D&AIMonthlyXOps"
									route='/D&AIXOps/Summary'
									open={openTileId === "D&AI-Monthly-XOps"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "D&AI-Monthly-XOps" ? null : "D&AI-Monthly-XOps"
										)
									}
									childrenDashboards={[
									{
										id: 'xops-summary',
										title: 'XOps Overview',
										route: '/D&AIXOps/Summary',
										icon: <DataOpsIcon width={24} height={24} />,
									},
									{
										id: 'xops-experience',
										title: 'Experience Management',
										route: '/D&AIXOps/experience/HappySignals',
										icon: <DataOpsIcon width={24} height={24} />,
									},
									{
										id: 'xops-it-ops-itsm-type',
										title: 'ITSM Ops Dashboards - By ITSM Type',
										route: '/D&AIXOps/itsm/MIMReports',
										icon: <DataOpsIcon width={24} height={24} />,
									},
									{
										id: 'xops-it-ops-segments',
										title: 'ITSM Ops Dashboards - By Segments',
										route: '/D&AIXOps/segments/DepartmentMetrics',
										icon: <DataOpsIcon width={24} height={24} />,

										},
										{
											id: 'xops-service',
											title: 'Service Operations',
											route: '/D&AIXOps/service/BAICenter',
											icon: <DataOpsIcon width={24} height={24} />,

										},
										{
											id: 'xops-comp-inv',
											title: 'D&AI Component Inventory',
											route: '/D&AIXOps/comp-inv/governance',
											icon: <DataOpsIcon width={24} height={24} />,
										},
										{
											id: 'xops-misc',
											title: 'Miscellaneous Dashboards',
											route: '/D&AIXOps/misc/topicOfTheMonth',
											icon: <DataOpsIcon width={24} height={24} />,
										},
									]}
									/>
								</Grid>
							)}
							{filteredCapabilities.includes('Data Ops') && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dashboard/dataOpsLanding')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/dashboard/dataOpsLanding');
								// }}
								>
									<DashboardTileComponent
										id="data-pipeline"
										title="Data Pipeline Observabilities"
										icon={<DataObservabilityLandingIcon width="60px" height="60px" />}
										iconKey="DataPipeline"
										open={openTileId === "data-pipeline"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "data-pipeline" ? null : "data-pipeline"
											)
										}
										childrenDashboards={[
											{
												id: "dp-sustain",
												title: "DataOps Sustain",
												route: "/data-pipeline/dataOps",
												tagline: "Monitor jobs or pipelines for a specific project.",
												icon: <DataOpsIcon width={24} height={24} />,
												iconKey: "dp-sustain"
											},
											{
												id: "dp-program",
												title: "Program Ops Dashboards",
												route: "/data-pipeline/programOpsDashboard",
												tagline: "Observe custom Grafana dashboards.",
												icon: <ProgramOpsIcon width={24} height={24} />,
												iconKey: "dp-program",
												hide: isRestricted
											},
											{
												id: "dp-end-to-end",
												title: "End to End Observability Dashboard",
												route: "/data-pipeline/home/executiveOps",
												tagline: "Monitor jobs or pipelines for a specific project.",
												icon: <SectorExecutiveIcon width={24} height={24} />,
												iconKey: "dp-end-to-end",
												hide: isRestricted,
												children: [
													{
														id: "end-to-end-sector",
														title: "Sector Dashboards",
														route: "/data-pipeline/executiveOps/sector",
														icon: <TopicOfTheMonthIcon width={24} height={24} />,
														iconKey: "end-to-end-sector",
														tagline: "Monitor jobs or pipelines for a specific project."
													},
													{
														id: "end-to-end-program",
														title: "Program Dashboards",
														route: "/data-pipeline/executiveOps/program",
														icon: <PlatformIcon width={24} height={24} />,
														iconKey: "end-to-end-program",
														tagline: "Monitor jobs or pipelines for a specific project."
													},
													{
														id: "end-to-end-data-recon",
														title: "Data Recon Dashboards",
														route: "/data-pipeline/executiveOps/pepsellPepIrisRecon",
														icon: <DataReconIcon color={'#003771'} />,
														iconKey: "end-to-end-data-recon",
														tagline: "Monitor the metrics and KPIs for a specific program."
													},

												],
											},

											{
												id: "PlatformHealth",
												title: "Platform Health",
												route: "/dataOps/platformHealth/summary",
												tagline: "Monitor platform health across PepsiCo for performance,growth and usage",
												icon: <img src={PlatformHealth} width="24px" height="24px" alt="Platform Health" />,
												iconKey: "PlatformHealth"
											},
											{
												id: "Alerts",
												title: "Pipeline Early Warning System",
												route: "/dataops/unified-alerts",
												tagline: "Monitor pipeline health across Pepsico for the alerts and early notifications.",
												icon: <AlertIcon width={24} height={24} />,
												iconKey: "Alerts"
											},
											{
												id: "Incidents",
												title: "Ops Pulse Dashboard",
												route: "/dataops/unified-incidents",
												tagline: "Unified view of data platform incidents across all observability tools.",
												icon: <IncidentsIcon width={24} height={24} />,
												iconKey: "Incidents"
											},
											{
												id: "PipelineRunStatus",
												title: "Integrated Job Monitoring Dashboard",
												route: "/dataOps/integratedJobMonitoring",
												tagline: "Realtime tracking of pipeline runs.",
												icon: <PipelineRunStatus width={24} height={24} />,
												iconKey: "PipelineRunStatus"
											},
											{
												id: "CrossFlowNavigator",
												title: "CrossFlow Navigator",
												route: "/dataOps/crossFlowNavigator",
												tagline: "Interactive graph and reports for enterprise data lineage.",
												icon: <DataOpsIcon width={24} height={24} />,
												iconKey: "CrossFlowNavigator"
											},
										]}
									/>
								</Grid>
							)}
							{filteredCapabilities.includes('IBP') &&
								(!hideIBPExec || !hideMDIPExec) && (
									<Grid
										item
										lg={3}
										md={4}
										sm={6}
										xs={12}
										style={{ position: 'relative', width: '100%' }}
									// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dashboard/programExecutiveLanding')}
									// onContextMenu={(e) => {
									// 	e.preventDefault();
									// 	handleDataPipelineClick(e, 'blank', '/dashboard/programExecutiveLanding');
									// }}
									>
										<DashboardTileComponent
											id="Program-Executive-Dashboards"
											title="Program Executive Dashboards"
											icon={<ProgramObsLandingIcon width="60px" height="60px" />}
											iconKey="ProgramExecutive"
											open={openTileId === "Program-Executive-Dashboards"}
											onToggle={() =>
												setOpenTileId(
													openTileId === "Program-Executive-Dashboards" ? null : "Program-Executive-Dashboards"
												)
											}
											childrenDashboards={[
												{
													id: "ibp",
													title: "Integrated Business Planning (IBP)",
													route: "/ibp/pipelines",
													tagline: "Monitoring the IBP Turkey data landscape across the EDAP platforms leveraged.",
													icon: <IbpIcon width={24} height={24} />,
													iconKey: "ibp",
													hide: hideIBPExec
												},
												{
													id: "mdip",
													title: "MDIP Executive Dashboard",
													route: "/mdip/dashboard",
													tagline: "Real-time dashboard-key metrics & insights for strategic decisions.",
													icon: <MdipIcon width={24} height={24} />,
													iconKey: "mdip",
													hide: hideMDIPExec
												}
											]}
										/>
									</Grid>
								)}
							{filteredCapabilities.includes('Fin Ops') && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dashboard/financeOpsLanding')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/dashboard/financeOpsLanding');
								// }}
							>
								<DashboardTileComponent
									id="Resource-Mapping-Dashboard"
									title="Resource Mapping Dashboard"
									icon={<RMLandingIcon width="60px" height="60px" />}
									iconKey="finops"
									open={openTileId === "Resource-Mapping-Dashboard"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "Resource-Mapping-Dashboard" ? null : "Resource-Mapping-Dashboard"
										)
									}
									childrenDashboards={[
										{
										id: "rm",
										title: "Resource Mapping",
										route: "/finops/resourcemapping/programs",
										tagline: "Monitor and update the resources.",
										icon: <RMIcon width={24} height={24} />,
										iconKey:"rm"
										},
										{
										id: "platform-cost",
										title: "Platform Cost",
										route: "/platformCostObs/dashboard",
										tagline: "View cost analysis across different sectors.",
										icon: <PlatformCostIcon width={24} height={24} />,
										iconKey: "platform-cost",
										hide: hideRMPlatformCost
										}
									]}
								/>
							</Grid>
						)}
						{isPlatformObsVisible && (
							<Grid
								item
								lg={3}
								md={4}
								sm={6}
								xs={12}
								style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dashboard/platformCostLanding')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/dashboard/platformCostLanding');
								// }}
								>
									<DashboardTileComponent
										id="Platform-Observability"
										title="Platform Observability"
										icon={<PlatformLandingIcon width="60px" height="60px" />}
										iconKey="PlatformObservability"
										open={openTileId === "Platform-Observability"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "Platform-Observability" ? null : "Platform-Observability"
											)
										}
										childrenDashboards={[
											{
												id: "Teradata-Jobs",
												title: "Teradata Jobs",
												route: "/dashboard/business",
												tagline: "Monitor jobs scheduled from 2 different schedulers: CTRL-m and Autosys.",
												icon: <TerradataIcon width={24} height={24} />,
												iconKey: "Teradata-Jobs",
												hide: hideTeradata
											},
											{
												id: "Alerts",
												title: "Pipeline Early Warning System",
												route: "/dataops/unified-alerts",
												tagline: "Monitor pipeline health across Pepsico for the alerts and early notifications.",
												icon: <AlertIcon width={24} height={24} />,
												iconKey: "Alerts"
											},
											{
												id: "Incidents",
												title: "Ops Pulse Dashboard",
												route: "/dataops/unified-incidents",
												tagline: "Unified view of data platform incidents across all observability tools.",
												icon: <IncidentsIcon width={24} height={24} />,
												iconKey: "Incidents"
											},
											{
												id: "PowerBI",
												title: "Power BI",
												route: "/powerBI/userActivity/dashboard",
												tagline: "Monitor and Manage Power BI capacities across PepsiCo for performance, growth and usage.",
												icon: <PowerBIIcon width={24} height={24} />,
												iconKey: "PowerBI"
											},
											{
												id: "Tableau",
												title: "Tableau",
												route: "/tableau/admin/dashboard",
												tagline: "Monitor and manage Tableau servers across PepsiCo for performance, growth and usage.",
												icon: <TableauIcon width={24} height={24} />,
												iconKey: "Tableau"
											},
											{
												id: "Data-Growth",
												title: "Data Growth",
												route: "/dataGrowth/azureStorage",
												tagline: "Analyze data growth trends to help teams understand, monitor, and manage their data across the full purview of Teradata.",
												icon: <DataGrowthIcon width={24} height={24} />,
												iconKey: "Data-Growth",
												hide: hideDG('some')
											},
											{
												id: "Databricks",
												title: "Databricks",
												route: "/databricksADB/workspace",
												tagline: "Monitor Databricks clusters across PepsiCo to identify usage trends, cost spikes and workspace health.",
												icon: <DataBricksIcon width={24} height={24} />,
												iconKey: "Databricks"
											},
											{
												id: "Presto",
												title: "Presto",
												route: "/presto/dashboard",
												tagline: "Monitor Presto usage by identifying long running queries trends of query execution and keep track of usage of data over a period.",
												icon: <PrestoIcon width={24} height={24} />,
												iconKey: "Presto",
												hide: hidePresto
											},
											{
												id: "Data-Management-Framework",
												title: "Data Management Framework",
												route: "/dataManagementFramework/healthSummary/dashboard",
												tagline: "Monitor the health of storage accounts across PepsiCo to identify duplicate, redundant and stale data with respect to files and tables. Verify the quality and completeness of data across sectors and projects.",
												icon: <DataManagementIcon width={24} height={24} />,
												iconKey: "Data-Management-Framework",
												hide: hideDMF
											},
											{
												id: "Enterprise-Platform-Modernization-Dashboard",
												title: "Enterprise Platform Modernization Dashboard",
												route: "/enterprisePlatform/dashboard",
												tagline: "Automated reports that help monitor, manage and ensure compliance of various legal and regulatory obligations across tools.",
												icon: <EnterprisePlatformIcon width={24} height={24} />,
												iconKey: "Enterprise-Platform-Modernization-Dashboard"
											}
										]}
									/>
								</Grid>
							)}
							{(!hideML && filteredCapabilities.includes('Machine Learning')) && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/machineLearning/dashboard')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/machineLearning/dashboard');
								// }}
								>
									<DashboardTileComponent
										id="ml"
										title="ML Observability"
										icon={<MLLandingIcon width="60px" height="60px" />}
										route="/machineLearning/dashboard"
										iconKey='mlObservability'
										open={openTileId === "ml"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "ml" ? null : "ml"
											)
										}
									/>
								</Grid>
							)}
							{filteredCapabilities.includes('Data KPI and Alerts') && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								// onClick={(e) => handleDataPipelineClick(e, 'no-blank', '/dashboard/alertKpiLanding')}
								// onContextMenu={(e) => {
								// 	e.preventDefault();
								// 	handleDataPipelineClick(e, 'blank', '/dashboard/alertKpiLanding');
								// }}
							>
								<DashboardTileComponent
									id="DataKPI-and-Alerts"
									title="Data KPI and Alerts"
									icon={<KPIandAlertsLandingIcon width="60px" height="60px" />}
									iconKey='DataKPI-and-Alerts'
									open={openTileId === "DataKPI-and-Alerts"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "DataKPI-and-Alerts" ? null : "DataKPI-and-Alerts"
										)
									}
									childrenDashboards={[
										{
										id: "KPIStore",
										title: "KPI Store",
										route: "/kpiFramework/configs",
										tagline: "Stay ahead with live monitoring of critical data KPIs.",
										icon: <KPIStoreIcon width={24} height={24} />,
										iconKey: "KPIStore"
										},
										{
										id: "Alert-Framework",
										title: "Alert Framework",
										route: "/alertFramework/alert",
										tagline: "Detect issues before they impact business with near real-time notifications.",
										icon: <AlertIcon width={24} height={24} />,
										iconKey: "Alert-Framework"
										}
									]}
									/>
								</Grid>
							)}
							{filteredCapabilities.includes('Product Catalog') && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								>
									<DashboardTileComponent
										id="Product-Catalog-Dashboard"
										title="Product Catalog Dashboard"
										icon={<ProductCatalogLandingIcon width="60px" height="60px" />}
										iconKey="ProductCatalog"
										route='/productCatalog/overview'
										open={openTileId === "Product-Catalog-Dashboard"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "Product-Catalog-Dashboard" ? null : "Product-Catalog-Dashboard"
											)
										}
									/>
								</Grid>
							)}
			{(isEnvTypePROD ? checkRole(role, FINOPS_ALL_ROLES) : true) && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								>
									<DashboardTileComponent
										id="finOps"
										title="FinOps Executive Dashboard"
										icon={<RMLandingIcon width="60px" height="60px" />}
										route="/finops/executive"
										iconKey='finops'
										open={openTileId === "finOps"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "finOps" ? null : "finOps"
											)
										}
									/>
								</Grid>
							)}
							{isPlatformObsVisible && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								>
									<DashboardTileComponent
										id="Product-Success-Dashboard"
										title="Product Success Dashboard"
										icon={<ProductSuccessLandingIcon width="60px" height="60px" />}
										iconKey="ProductSuccess"
										route='/productSuccessDashboard/userAdoption'
										open={openTileId === "Product-Success-Dashboard"}
										onToggle={() =>
											setOpenTileId(
												openTileId === "Product-Success-Dashboard" ? null : "Product-Success-Dashboard"
											)
										}
									/>
								</Grid>
							)}
							<Grid
								item
								lg={3}
								md={4}
								sm={6}
								xs={12}
								style={{ position: 'relative', width: '100%' }}
							>
								<DashboardTileComponent
									id="Product-Catalog-Dashboard"
									title="Product Catalog"
									icon={<ProductCatalogLandingIcon width="60px" height="60px" />}
									iconKey="ProductCatalog"
									route='/productCatalog/overview'
									open={openTileId === "Product-Catalog-Dashboard"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "Product-Catalog-Dashboard" ? null : "Product-Catalog-Dashboard"
										)
									}
								/>
							</Grid>
							{(role === 'Admin' || (Array.isArray(role) && role.includes('Admin'))) && (
								<Grid
									item
									lg={3}
									md={4}
									sm={6}
									xs={12}
									style={{ position: 'relative', width: '100%' }}
								>
									<DashboardTileComponent
										id="creds"
										title="Pepwise Credential Management"
										icon={<DataXopsLandingIcon width="60px" height="60px" />}
										route="/credsManagement/summary"
										iconKey="creds"
										open={openTileId === 'creds'}
										onToggle={() =>
											setOpenTileId(openTileId === 'creds' ? null : 'creds')
										}
									/>
								</Grid>
							)}
							<Grid
								item
								lg={3}
								md={4}
								sm={6}
								xs={12}
								style={{ position: 'relative', width: '100%' }}
							>
								<DashboardTileComponent
									id="demoV2"
									title="Components Library"
									icon={<DataXopsLandingIcon width="60px" height="60px" />}
									route="/demo-dashboard"
									iconKey=''
									open={openTileId === "demoV2"}
									onToggle={() =>
										setOpenTileId(
											openTileId === "demoV2" ? null : "demoV2"
										)
									}
								/>
							</Grid>
						</Grid>
					</Grid>
					{/* <Footer /> */}
				</Suspense>
		</Box>
		</>
	);
};

export default LandingPage;